/**
 * Social Embed Renderer
 *
 * Generates HTML for social media embeds with GDPR consent overlay.
 * Each platform uses its native embed format (oEmbed blockquotes, iframes, etc.)
 */

import { logger } from "~/lib/logger/logger";
import { escapeHtml, sanitizeUrl, escapeAttribute } from "~/utils/htmlSecurity";
import {
  type SocialPlatform,
  PLATFORM_ICONS,
  PLATFORM_COLORS,
  PLATFORM_INFO,
  extractMediaId,
} from "./socialPlatforms";

/**
 * Renders the actual embed content HTML (without consent wrapper)
 *
 * Each platform has specific embed requirements:
 * - Instagram/Twitter/TikTok: oEmbed blockquotes that get enhanced by platform scripts
 * - YouTube/Spotify/LinkedIn: Direct iframe embeds
 * - Facebook: Plugin iframe with encoded URL
 * - Threads: Simple link (no embed API available)
 *
 * @internal
 */
function renderEmbedContent(url: string, platform: SocialPlatform, mediaId: string | null): string {
  const containerClass = "social-embed";
  const platformClass = `social-embed--${platform}`;

  switch (platform) {
    case "instagram":
      return `
        <div class="${containerClass} ${platformClass}">
          <blockquote
            class="instagram-media"
            data-instgrm-captioned
            data-instgrm-permalink="${escapeAttribute(url)}"
            style="max-width:540px; min-width:326px; width:100%;"
          >
            <a href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer">View on Instagram</a>
          </blockquote>
        </div>
      `;

    case "x":
      return `
        <div class="${containerClass} ${platformClass}">
          <blockquote class="twitter-tweet">
            <a href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer">View on X</a>
          </blockquote>
        </div>
      `;

    case "youtube":
      if (mediaId) {
        return `
          <div class="${containerClass} ${platformClass}">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/${escapeAttribute(mediaId)}"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              loading="lazy"
            ></iframe>
          </div>
        `;
      }
      return "";

    case "tiktok":
      return `
        <div class="${containerClass} ${platformClass}">
          <blockquote
            class="tiktok-embed"
            cite="${escapeAttribute(url)}"
            data-video-id="${escapeAttribute(mediaId || "")}"
            style="max-width:605px; min-width:325px;"
          >
            <a href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer">View on TikTok</a>
          </blockquote>
        </div>
      `;

    case "facebook": {
      // If the URL is already a Facebook plugin embed URL (e.g. extracted from
      // a pasted <iframe> by normalizeEmbedUrl), use it directly to avoid
      // double-wrapping in plugins/post.php.
      const iframeSrc = url.includes("facebook.com/plugins/")
        ? url
        : `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500`;
      return `
        <div class="${containerClass} ${platformClass}">
          <iframe
            src="${escapeAttribute(iframeSrc)}"
            width="500"
            height="600"
            style="border:none;overflow:hidden"
            scrolling="no"
            frameborder="0"
            allowfullscreen="true"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            loading="lazy"
          ></iframe>
        </div>
      `;
    }

    case "threads":
      return `
        <div class="${containerClass} ${platformClass}">
          <a href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer" class="social-embed__link">
            View on Threads
          </a>
        </div>
      `;

    case "spotify": {
      const spotifyMatch = url.match(
        /spotify\.com\/(track|album|playlist|episode|show)\/([a-zA-Z0-9]+)/
      );
      if (spotifyMatch) {
        const [, type, id] = spotifyMatch;
        return `
          <div class="${containerClass} ${platformClass}">
            <iframe
              style="border-radius:12px"
              src="https://open.spotify.com/embed/${escapeAttribute(type)}/${escapeAttribute(id)}"
              width="100%"
              height="${type === "track" ? "152" : "352"}"
              frameborder="0"
              allowfullscreen=""
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            ></iframe>
          </div>
        `;
      }
      return "";
    }

    case "linkedin": {
      // LinkedIn embed URL format: /embed/feed/update/urn:li:{type}:{id}
      // Try explicit URN URLs first, then extract URN from /posts/ slug
      const urnMatch =
        url.match(/urn:li:(activity|share|ugcPost):(\d+)/) ||
        url.match(/[-_](ugcPost|activity|share)[-_](\d+)/);

      if (urnMatch) {
        const embedSrc = `https://www.linkedin.com/embed/feed/update/urn:li:${urnMatch[1]}:${urnMatch[2]}`;
        return `
          <div class="${containerClass} ${platformClass}">
            <iframe
              src="${escapeAttribute(embedSrc)}"
              width="504"
              height="500"
              frameborder="0"
              allowfullscreen=""
              title="LinkedIn Post"
              loading="lazy"
            ></iframe>
          </div>
        `;
      }

      // No embeddable URN found — render as a fallback link
      return "";
    }

    default:
      return `
        <div class="${containerClass} social-embed--fallback">
          <a href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer" class="social-embed__link">
            ${escapeHtml(url)}
          </a>
        </div>
      `;
  }
}

/**
 * Renders the GDPR consent overlay HTML
 *
 * German privacy law (DSGVO) requires explicit consent before loading
 * third-party content that may set cookies or transmit data.
 *
 * The overlay shows:
 * - Platform icon and name
 * - Privacy explanation in German
 * - Link to platform's privacy policy
 * - "Load content" and "Always load" buttons
 *
 * @internal
 */
function renderConsentOverlay(_url: string, platform: SocialPlatform): string {
  const info = PLATFORM_INFO[platform] || PLATFORM_INFO.unknown;
  const icon = PLATFORM_ICONS[platform] || PLATFORM_ICONS.unknown;
  const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.unknown;

  return `<div class="embed-consent-overlay" data-platform="${platform}"><div class="embed-consent-overlay__icon-wrapper" style="background-color: ${color}"><span class="embed-consent-overlay__icon">${icon}</span></div><h3 class="embed-consent-overlay__title">${info.name}-Inhalt</h3><p class="embed-consent-overlay__description">Mit dem Laden des Inhalts akzeptierst du die Datenschutzerklärung von ${info.name}. Es werden möglicherweise Cookies gesetzt und Daten an Dritte übermittelt.</p><a href="${info.privacyUrl}" target="_blank" rel="noopener noreferrer" class="embed-consent-overlay__privacy-link">Mehr erfahren</a><div class="embed-consent-overlay__actions"><button type="button" class="embed-consent-overlay__button embed-consent-overlay__button--primary" data-consent-action="accept" data-consent-platform="${platform}">Inhalt laden</button><button type="button" class="embed-consent-overlay__button embed-consent-overlay__button--secondary" data-consent-action="accept-all">Alle ${info.name}-Inhalte laden</button></div></div>`;
}

/**
 * Normalizes common CMS input mistakes for embed URLs:
 * - Extracts `src` from pasted iframe HTML (e.g. LinkedIn embed codes)
 * - Prepends `https://` when the protocol is missing (e.g. `www.facebook.com/...`)
 *
 * @internal
 */
function normalizeEmbedUrl(raw: string): string {
  const trimmed = raw.trim();

  // Extract src from pasted <iframe> HTML
  const iframeSrcMatch = trimmed.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  if (iframeSrcMatch) {
    return iframeSrcMatch[1];
  }

  // Prepend https:// for bare domain URLs (www.example.com/...)
  if (/^www\./i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return trimmed;
}

/**
 * Renders a social media embed with GDPR consent overlay
 *
 * The embed content is hidden by default and only shown after user consent.
 * Consent is managed by Usercentrics per vendor.
 *
 * Flow:
 * 1. Validate URL (only http/https allowed)
 * 2. Extract media ID for platforms that need it
 * 3. Generate platform-specific embed HTML
 * 4. Wrap in consent overlay (except for threads/unknown which are just links)
 * 5. Store embed HTML as base64 in data attribute for later reveal
 *
 * @param url - Social media URL to embed
 * @param platform - Detected platform type
 * @returns HTML string with consent wrapper or direct embed
 */
export function renderSocialEmbed(url: string, platform: SocialPlatform): string {
  // Normalize common CMS input mistakes before strict validation
  const normalizedUrl = normalizeEmbedUrl(url);

  // Validate URL before processing - only allow http/https for embeds
  const sanitizedUrl = sanitizeUrl(normalizedUrl);
  if (
    sanitizedUrl === "#" ||
    (!sanitizedUrl.startsWith("http://") && !sanitizedUrl.startsWith("https://"))
  ) {
    logger.warn("[socialEmbedRenderer] Invalid social embed URL:", url);
    return "";
  }

  const mediaId = extractMediaId(sanitizedUrl, platform);
  const embedContent = renderEmbedContent(sanitizedUrl, platform, mediaId);

  // For link-only platforms (threads, unknown), skip consent
  if (platform === "threads" || platform === "unknown") {
    return embedContent;
  }

  // If no valid embed content, return fallback
  if (!embedContent) {
    const escapedUrl = escapeAttribute(sanitizedUrl);
    return `
      <div class="social-embed social-embed--fallback">
        <a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="social-embed__link">
          ${escapeHtml(sanitizedUrl)}
        </a>
      </div>
    `;
  }

  // Encode the embed content as base64 to store in data attribute
  const encodedContent =
    typeof window !== "undefined"
      ? btoa(encodeURIComponent(embedContent))
      : Buffer.from(encodeURIComponent(embedContent)).toString("base64");

  // Escape URL for safe use in HTML attributes
  const escapedUrl = escapeAttribute(sanitizedUrl);

  return `<div class="embed-consent-wrapper" data-platform="${platform}" data-embed-url="${escapedUrl}" data-embed-content="${encodedContent}">${renderConsentOverlay(sanitizedUrl, platform)}<div class="embed-consent-content"></div></div>`;
}
